import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import HalloweenBanner from "../components/HalloweenBanner";
import { api } from "../lib/api";
import { isOnSale, salePercentOff, effectivePrice, saleCountdown } from "../lib/sale";

const CATEGORY_LABELS = {
  bundle: "Kits",
  hoodie: "Hoodies",
  tracksuit: "Tracksuits",
  tee: "Tees",
  shirt: "Shirts",
  polo: "Polos",
  sweatpants: "Sweatpants",
  shorts: "Shorts",
  outerwear: "Outerwear",
  pants: "Pants",
  accessory: "Accessories",
};
// Display order — apparel first, accessories last
const CATEGORY_ORDER = [
  "bundle",
  "hoodie",
  "tracksuit",
  "outerwear",
  "tee",
  "shirt",
  "polo",
  "sweatpants",
  "shorts",
  "pants",
  "accessory",
];

const CATEGORIES = [
  { id: "", label: "ALL" },
  { id: "bundle", label: "KITS" },
  { id: "hoodie", label: "HOODIES" },
  { id: "tracksuit", label: "TRACKSUITS" },
  { id: "tee", label: "TEES" },
  { id: "shirt", label: "SHIRTS" },
  { id: "polo", label: "POLOS" },
  { id: "sweatpants", label: "SWEATPANTS" },
  { id: "shorts", label: "SHORTS" },
  { id: "outerwear", label: "OUTERWEAR" },
  { id: "pants", label: "PANTS" },
  { id: "accessory", label: "ACCESSORIES" },
];

const COLOR_HEX = {
  BLACK: "#0a0a0a",
  WHITE: "#fafafa",
  PINK: "#ec4899",
  RED: "#dc2626",
  GREEN: "#16a34a",
  OLIVE: "#556b2f",
  LAVENDER: "#c4b5fd",
  "GREY/NAVY": "linear-gradient(90deg, #9ca3af 0%, #9ca3af 50%, #1e3a8a 50%, #1e3a8a 100%)",
};

const SORT_OPTS = [
  { id: "featured", label: "DEFAULT" },
  { id: "price-asc", label: "PRICE LOW → HIGH" },
  { id: "price-desc", label: "PRICE HIGH → LOW" },
  { id: "name", label: "NAME A-Z" },
];

const ShopPage = () => {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const halloween = params.get("halloween") === "true";
  const sortBy = params.get("sort") || "featured";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category) q.category = category;
    if (halloween) q.halloween_drop = true;
    api.get("/products", { params: q })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, halloween]);

  const sortedProducts = React.useMemo(() => {
    const arr = [...products];
    if (sortBy === "price-asc") arr.sort((a, b) => effectivePrice(a) - effectivePrice(b));
    else if (sortBy === "price-desc") arr.sort((a, b) => effectivePrice(b) - effectivePrice(a));
    else if (sortBy === "name") arr.sort((a, b) => a.name.localeCompare(b.name));
    else {
      // Default: on-sale first, then featured/limited, then rest
      arr.sort((a, b) => {
        const aSale = isOnSale(a) ? 1 : 0;
        const bSale = isOnSale(b) ? 1 : 0;
        if (aSale !== bSale) return bSale - aSale;
        return (b.featured === a.featured ? 0 : b.featured ? 1 : -1);
      });
    }
    return arr;
  }, [products, sortBy]);

  // Tick every second so countdown badges stay live
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const anySale = products.some((p) => isOnSale(p) && p.sale_ends_at);
    if (!anySale) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [products]);

  // Group products by category for the "ALL" view (no specific filter)
  const grouped = React.useMemo(() => {
    if (category || halloween) return null; // only for the raw ALL view
    const byCat = {};
    for (const p of sortedProducts) {
      const key = p.category || "other";
      (byCat[key] = byCat[key] || []).push(p);
    }
    return CATEGORY_ORDER
      .filter((k) => byCat[k]?.length)
      .map((k) => ({ key: k, label: CATEGORY_LABELS[k] || k, items: byCat[k] }));
  }, [sortedProducts, category, halloween]);

  // Product card block (used in both grouped + flat views)
  const renderCard = (p) => {
    const onSale = isOnSale(p);
    const countdown = saleCountdown(p, nowMs);
    const pct = salePercentOff(p);
    const soldOut = !!p.sold_out || (p.stock ?? 0) <= 0;
    return (
      <Link
        key={p.id}
        to={`/shop/${p.slug}`}
        data-testid={`product-card-${p.slug}`}
        className={`group border-2 bg-zinc-950 transition-all duration-300 ${
          p.category === "mystery"
            ? "border-red-900/60 hover:border-red-500 hover:mystery-glow"
            : "border-zinc-800 hover:border-zinc-300 hover:shadow-[0_0_0_1px_rgba(212,212,216,0.35),0_20px_50px_-20px_rgba(212,212,216,0.5)]"
        }`}
      >
        <div className="aspect-square bg-zinc-900 overflow-hidden relative">
          <img
            src={p.image_url}
            alt={p.name}
            className={`w-full h-full object-cover transition-all duration-500 ${
              soldOut ? "grayscale opacity-50 blur-[7px] scale-105" : "group-hover:scale-105"
            }`}
          />
          {soldOut && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-950/55 pointer-events-none"
              data-testid={`sold-out-overlay-${p.slug}`}
            >
              <div className="w-[115%] bg-zinc-950/95 border-y-2 border-red-600 py-4 -rotate-[8deg] shadow-[0_20px_60px_-10px_rgba(220,38,38,0.7)] text-center">
                <div className="font-display text-3xl sm:text-4xl text-red-500 tracking-[0.35em] uppercase leading-none">Sold Out</div>
                <div className="font-mono-tech text-[9px] text-zinc-400 tracking-[0.4em] uppercase mt-1.5">// Restock Unavailable</div>
              </div>
            </div>
          )}
          {onSale && !soldOut && (
            <span
              className="absolute top-2 right-2 bg-red-600 text-zinc-50 font-mono-tech text-xs px-2 py-0.5 uppercase tracking-widest"
              data-testid={`sale-badge-${p.slug}`}
            >
              {pct > 0 ? `-${pct}%` : "SALE"}
            </span>
          )}
          {p.limited_edition && !soldOut && (
            <span className={`absolute top-2 left-2 font-mono-tech text-xs px-2 py-0.5 uppercase tracking-widest ${
              p.category === "mystery" ? "bg-red-600 text-zinc-50" : "bg-zinc-200 text-zinc-950"
            }`}>
              {p.category === "mystery" ? "Sealed" : "Limited"}
            </span>
          )}
          {countdown && !soldOut && (
            <span
              className="absolute bottom-2 right-2 bg-zinc-950/85 border border-red-600 text-red-400 font-mono-tech text-[10px] px-2 py-0.5 uppercase tracking-widest"
              data-testid={`sale-countdown-${p.slug}`}
            >
              ENDS IN {countdown}
            </span>
          )}
          {p.colors && p.colors.length > 0 && !soldOut && p.category !== "mystery" && (
            <div className="absolute bottom-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`swatch-row-${p.slug}`}>
              {p.colors.slice(0, 5).map((c) => (
                <span
                  key={c}
                  title={c}
                  className="w-4 h-4 border-2 border-zinc-700"
                  style={{ background: COLOR_HEX[c] || "#71717a" }}
                />
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t-2 border-zinc-800">
          <div className="flex justify-between items-start gap-4">
            <div>
              <div className="font-mono-tech text-xs text-zinc-500 uppercase">{p.category}</div>
              <div className="font-display text-xl text-zinc-50 mt-1 uppercase tracking-wider">{p.name}</div>
            </div>
            <div className="font-mono-tech text-sm whitespace-nowrap text-right">
              {soldOut ? (
                <span className="text-zinc-600 line-through">${p.price.toFixed(2)}</span>
              ) : onSale ? (
                <>
                  <span className="text-zinc-500 line-through mr-2">${p.price.toFixed(2)}</span>
                  <span className="text-red-500">${Number(p.sale_price).toFixed(2)}</span>
                </>
              ) : (
                <span className="text-zinc-200">${p.price.toFixed(2)}</span>
              )}
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between font-mono-tech text-xs uppercase">
            <span className={soldOut ? "text-zinc-600" : "text-zinc-500"}>{p.sizes.join(" / ")}</span>
            {soldOut ? (
              <span className="text-rose-500 tracking-widest font-semibold" data-testid={`sold-out-label-${p.slug}`}>Sold Out</span>
            ) : p.category === "mystery" ? (
              <span className="text-red-400 tracking-widest">SEALED</span>
            ) : p.colors && p.colors.length > 0 && (
              <span className="text-zinc-400">{p.colors.length} {p.colors.length === 1 ? "COLOR" : "COLORS"}</span>
            )}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <HalloweenBanner />
      <NavBar />

      {/* Hero strip */}
      <section className="relative border-b border-zinc-800 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1631197344054-e0fab87e541a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover grayscale contrast-125 opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/50 via-zinc-950/80 to-zinc-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-4">
            Drop 002 · Inventory
          </p>
          <h1 className="font-syne font-bold text-5xl sm:text-7xl md:text-8xl uppercase text-zinc-50 leading-[0.9]">The&nbsp;Shop</h1>
          <p className="mt-5 text-zinc-400 max-w-xl font-editorial text-base leading-relaxed">Premium streetwear. Limited drops. Once gone, gone forever.</p>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b-2 border-zinc-800 pb-6">
          <div className="flex flex-wrap gap-2" data-testid="shop-filters">
            <button
              key="halloween"
              type="button"
              data-testid="filter-halloween"
              onClick={() => {
                const next = {};
                next.halloween = "true";
                if (sortBy && sortBy !== "featured") next.sort = sortBy;
                setParams(next);
              }}
              className={`relative font-editorial font-semibold text-[11px] uppercase tracking-widest px-4 py-2 border transition-all ${
                halloween
                  ? "border-orange-400 bg-orange-500 text-zinc-950 shadow-[0_0_20px_rgba(249,115,22,0.5)]"
                  : "border-orange-500/60 text-orange-400 hover:border-orange-400 hover:bg-orange-500/15 hover:shadow-[0_0_14px_rgba(249,115,22,0.35)]"
              }`}
            >
              HALLOWEEN
            </button>
            {CATEGORIES.map((c) => {
              const active = !halloween && category === c.id;
              const accent = c.accent;
              const base = "font-editorial font-semibold text-[11px] uppercase tracking-widest px-4 py-2 border transition-colors";
              let styling;
              if (accent) {
                styling = active
                  ? "border-rose-800 bg-rose-900 text-zinc-50"
                  : "border-rose-900/60 text-rose-400 hover:border-rose-700 hover:bg-rose-900/20";
              } else {
                styling = active
                  ? "border-zinc-200 bg-zinc-100 text-zinc-950"
                  : "border-zinc-800 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100";
              }
              return (
                <button
                  key={c.id || "all"}
                  type="button"
                  data-testid={`filter-${c.id || "all"}`}
                  onClick={() => {
                    const next = {};
                    if (c.id) next.category = c.id;
                    if (sortBy && sortBy !== "featured") next.sort = sortBy;
                    setParams(next);
                  }}
                  className={`${base} ${styling}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono-tech text-xs uppercase text-zinc-500">Sort</span>
            <select
              value={sortBy}
              onChange={(e) => {
                const next = {};
                if (category) next.category = category;
                if (e.target.value && e.target.value !== "featured") next.sort = e.target.value;
                setParams(next);
              }}
              data-testid="shop-sort"
              className="bg-zinc-900 border-2 border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-xs text-zinc-100 uppercase"
            >
              {SORT_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Products grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="loading-skeleton">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="border-2 border-zinc-800 bg-zinc-950">
                <div className="aspect-square skeleton" />
                <div className="p-4 border-t-2 border-zinc-800 space-y-2">
                  <div className="h-3 w-16 skeleton" />
                  <div className="h-5 w-40 skeleton" />
                  <div className="h-3 w-24 skeleton" />
                </div>
              </div>
            ))}
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="py-20 text-center font-editorial text-zinc-500 uppercase text-sm tracking-widest" data-testid="empty-state">
            No items in this category.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-10" data-testid="products-grid">
            {sortedProducts.map(renderCard)}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default ShopPage;
